import React, { useState } from 'react'
import axios from 'axios'
import { Mail, Send, X } from 'lucide-react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import Editor from '../../../../../_component/Editor'
import {toast} from 'react-toastify'

type Props = {
  show: boolean
  handleClose: () => void
  selectedApplicants: any[]
  onSuccess: () => void
}

const getBaseUrl = () => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  if (url === 'http://localhost:3000') url = 'http://localhost:3000/api';
  return url;
}
const API_URL = getBaseUrl() + '/application/bulk-email'

const bulkEmailSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  message: Yup.string().required('Message is required'),
})

export const BulkEmailModal: React.FC<Props> = ({ show, handleClose, selectedApplicants, onSuccess }) => {
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (values: { subject: string, message: string }) => {
    setLoading(true)

    try {
      await axios.post(API_URL, {
        ids: selectedApplicants.map(a => a.id),
        subject: values.subject,
        message: values.message,
      })
      toast.success('Emails dispatched successfully!')
      onSuccess()
      handleClose()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send emails')
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className='fixed inset-0 z-[1050] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 md:p-8'>
      <div className='bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200'>
        <div className='bg-[#008080] text-white py-4 px-6 relative flex-shrink-0'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-full bg-white/20 flex items-center justify-center'>
              <Mail size={20} />
            </div>
            <div>
              <h5 className='modal-title text-white font-bold mb-0'>Send Bulk Email</h5>
              <p className='text-white/70 text-xs mb-0'>{selectedApplicants.length} recipients selected</p>
            </div>
          </div>
          <button
            type='button'
            className='absolute top-6 right-6 text-white/70 hover:text-white transition-colors'
            onClick={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        <Formik
          initialValues={{ subject: '', message: '' }}
          validationSchema={bulkEmailSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form className='flex flex-col flex-1 min-h-0 bg-white'>
              <div className='modal-body p-8 overflow-y-auto flex-1 custom-scrollbar'>

                <div className='mb-8 space-y-2'>
                  <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Email Subject</label>
                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors'>
                      <Mail size={18} className='text-gray-400 group-focus-within:text-[#008080]' />
                    </div>
                    <input
                      type='text'
                      name='subject'
                      className={`block w-full pl-11 pr-4 py-3.5 bg-gray-50/50 border ${errors.subject && touched.subject ? 'border-red-500 bg-red-50/20' : 'border-gray-200'} rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-[#008080]/20 focus:border-[#008080] focus:bg-white transition-all outline-none font-medium`}
                      placeholder='Enter professional subject line'
                      value={values.subject}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.subject && touched.subject && (
                    <div className='text-red-500 text-[10px] font-bold uppercase tracking-widest ml-1 mt-1'>
                      {errors.subject}
                    </div>
                  )}
                </div>

                <div className='mb-6 space-y-2'>
                  <div className='flex items-center justify-between mb-1'>
                    <label className='block text-xs font-black text-gray-400 uppercase tracking-widest'>Message Content</label>

                   <p className='text-xs mb-0 leading-relaxed font-semibold'>
                        Use the <strong className='font-extrabold mx-1 text-base'>{`{{name}}`}</strong> placeholder to automatically address each applicant by their full name. 
                      </p>
                  </div>
                    <Editor
                      name='message'
                      editorProps={{
                        placeholderList: [{ id: 'name', label: 'Applicant Name' }]
                      }}
                    />
                  {errors.message && touched.message && (
                    <div className='text-red-500 text-[10px] font-bold uppercase tracking-widest ml-1 mt-1'>
                      {errors.message}
                    </div>
                  )}
                </div>

                <div className='bg-gray-50 p-6 rounded-2xl border border-gray-100'>
                  <div className='flex items-center justify-between mb-4'>
                    <h4 className='text-xs font-black text-gray-400 uppercase tracking-widest'>Selected Recipients</h4>
                  </div>
                  <div className='flex flex-col gap-2 max-h-[160px] overflow-y-auto custom-scrollbar p-1'>
                    {selectedApplicants.map((app) => (
                      <div key={app.id} className='flex items-center justify-between bg-white px-4 py-2.5 rounded-xl border border-gray-100 shadow-sm transition-all hover:border-[#008080]/30 hover:bg-gray-50/50 group'>
                        <div className='flex items-center gap-3'>
                          <div className='w-1.5 h-1.5 rounded-full bg-[#008080]/40 group-hover:bg-[#008080] transition-colors'></div>
                          <span className='text-sm font-bold text-gray-800'>{app.full_name}</span>
                        </div>
                        <span className='text-xs font-medium text-gray-400 group-hover:text-[#008080]/70 transition-colors'>{app.email}</span>
                      </div>
                    ))}
                  </div>
                  <p className='text-[10px] text-gray-400 mt-4 italic font-medium'>
                    Emails will be personalized automatically for each candidate listed above.
                  </p>
                </div>
              </div>

              <div className='modal-footer border-t border-gray-100 p-8 flex justify-end gap-4 bg-gray-50/30 flex-shrink-0'>
                <button
                  type='button'
                  className='px-8 py-3.5 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 shadow-sm'
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='px-8 py-3.5 bg-[#008080] text-white text-sm font-black rounded-xl hover:bg-[#006666] transition-all duration-200 flex items-center gap-3 shadow-xl shadow-teal-500/20 disabled:opacity-75 disabled:cursor-not-allowed group'
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} className='group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200' />
                      Dispatch Emails
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
